import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountBalance, SideDrawer, withStyles } from 'vopay';
import { Button } from 'UI';
import useFetchPartnerAccountDetails from '@/components/Entities/hooks/useApi/useFetchPartnerAccountDetails';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { useToast } from '@/providers/Toast.provider';
import AccountCard from '@/components/Entities/Accounts/AccountHeaderCards/AccountCard';
import AvailableBalanceCard from '@/components/Entities/Accounts/AccountHeaderCards/AvailableBalanceCard';
import TransactionLimitsCard from '@/components/Entities/Accounts/AccountHeaderCards/TransactionLimitsCard';
import { getAccountCredentials } from '@/components/Entities/Accounts/AccountHeaderCards/accountCredentials';
import { getOutboundLimits } from '@/components/Entities/Accounts/AccountHeaderCards/accountLimits';
import styles from './AccountDetailsDrawer.module.scss';

const cn = withStyles(styles);

type AccountDetailsDrawerProps = {
  /** The list row the drawer was opened from; null keeps the drawer closed. */
  account: AccountBalance | null;
  onClose: () => void;
  onViewFullDetails: (account: AccountBalance) => void;
};

/**
 * The quick view of an account opened from its name in the list (spec 5.5): identity and
 * credentials, balances, and transaction limits, with the route to the full details page. The row
 * already carries the name, status and balances; the credentials, reserve policy and limit usage
 * are fetched on open.
 */
export default function AccountDetailsDrawer({ account, onClose, onViewFullDetails }: AccountDetailsDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountDetailsDrawer' });
  const toast = useToast();
  const { canSeeAccountCredential } = useModuleFeatureFlag('accounts');
  const { account: details, fetchPartnerAccountDetails, reset } = useFetchPartnerAccountDetails();
  const [hasDetailsFailed, setHasDetailsFailed] = useState(false);

  const accountId = account?.AccountID;

  useEffect(() => {
    // The previous account's details must not show under a newly opened account's name.
    reset();
    setHasDetailsFailed(false);

    if (!accountId) return undefined;

    // The hook drops a superseded response, but its rejection still reaches this caller — so a
    // request the user has already moved on from must not mark the open account as failed.
    let isCurrentAccount = true;

    fetchPartnerAccountDetails(accountId).catch((error) => {
      if (!isCurrentAccount) return;

      // The hook has logged the cause. The cards still render what the row carries; the policy and
      // usage figures show as unavailable rather than loading forever.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      setHasDetailsFailed(true);

      toast.neutral(
        t('loadError', {
          defaultValue: 'Could not load the account details, so some fields are unavailable: {{errorMessage}}',
          errorMessage,
        }),
      );
    });

    return () => {
      isCurrentAccount = false;
    };
  }, [accountId]);

  // Undefined while loading, null when the request failed — the cards render each differently.
  const detailsOrFailure = details ?? (hasDetailsFailed ? null : undefined);

  // Same gate as the account details page: an Active account, and a partner allowed to see credentials.
  const credentials =
    account && account.IsActive && canSeeAccountCredential ? getAccountCredentials(account.Token) : null;

  return (
    <SideDrawer
      isOpen={account !== null}
      onClose={onClose}
      title={t('title', { defaultValue: 'Account Details' })}
      containerClassName={cn('drawer')}
      footer={
        <Button
          colorScheme="primary"
          size="large"
          className={cn('viewFullDetails')}
          onClick={() => account && onViewFullDetails(account)}
        >
          {t('viewFullDetails', { defaultValue: 'View Full Details' })}
        </Button>
      }
    >
      {account && (
        <div className={cn('cards')}>
          <AccountCard account={account} credentials={credentials} />

          <AvailableBalanceCard
            currency={account.Currency}
            availableBalance={account.AvailableBalance}
            pendingBalance={account.PendingBalance}
            reserve={account.Reserve}
            rollingReserves={detailsOrFailure && (detailsOrFailure.RollingReserves ?? [])}
          />

          <TransactionLimitsCard
            currency={account.Currency}
            limits={detailsOrFailure && getOutboundLimits(detailsOrFailure)}
            usage={detailsOrFailure && (detailsOrFailure.LimitUsage?.Outbound ?? null)}
          />
        </div>
      )}
    </SideDrawer>
  );
}
